import "antd/dist/antd.css";
import "../App.css";

import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideCtaBand from "../components/GuideCtaBand";
import CtaCard from "../components/CtaCard";
import RelatedGuides from "../components/RelatedGuides";
import TripCostCalculator from "../components/TripCostCalculator";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "europe-trip-cost")!;

function EuropeTripCostGuide() {
    usePageMeta({
        title:
            "What will a Europe motorbike trip cost? Interactive calculator | Planamoto",
        description:
            "Set your days, accommodation, distance, meals and Channel crossing for a live estimate of what a European motorbike tour costs — built on average prices for each choice.",
        url: "https://www.planamoto.com/guides/europe-trip-cost",
    });

    return (
        <GuideArticle
            eyebrow="Interactive tool"
            title="What will a Europe motorbike trip cost?"
            lede="Set the shape of your trip below — days, where you sleep, how far you ride, whether you eat out, and how you cross the Channel — and watch a rough total build in real time. Every figure is an average for the choice you've made; the breakdown shows exactly where the money goes."
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                description: META.summary,
            }}
        >
            <TripCostCalculator />

            <GuideSection
                index={1}
                heading="How the estimate works"
                intro="These are deliberately rough, typical figures for a mid-range European tour — enough to plan a budget around, not a quote. Your real trip will swing with season, exchange rate and how well you eat."
                items={[
                    {
                        title: "Accommodation, per night",
                        body: "Counted as one night fewer than your days — you ride home on the last day — so a 7-day trip is 6 nights. Camping a small pitch ≈ £18. Hotels by rating: 2★ ≈ £70, 3★ ≈ £100, 4★ ≈ £150, 5★ ≈ £240 — a room for one rider. Booking early and avoiding August drops these; a festival town in peak season blows past them.",
                    },
                    {
                        title: "Fuel, from your distance",
                        body: "Worked at 55 mpg and £1.45/L petrol — a fully-loaded middleweight on mixed roads. Sports bikes ridden hard drink more; a small single sips less. Distance is the whole trip, so include the miles home. See the fuel-prices guide for live pump prices by country.",
                    },
                    {
                        title: "Meals, per day",
                        body: "Lunch ≈ £14 (a café stop or a supermarket picnic averaged out), dinner ≈ £28 (a sit-down meal with a drink). Untick either if you're self-catering at a campsite or living on baguettes and cheese.",
                    },
                    {
                        title: "The Channel crossing, return",
                        body: "A Dover–Calais ferry with bike and rider ≈ £150 return; Eurotunnel Le Shuttle ≈ £190 return and much quicker. Book flexible fares and both climb. Pick \"already there\" if you're starting on the continent.",
                    },
                    {
                        title: "Toll roads — budget these separately",
                        body: "Tolls aren't in the total — they swing too much by route to average cleanly. Rough guide: France's autoroutes cost a motorbike roughly £20–35 for a solid day of motorway and Italy's autostrade are similar by distance, while Switzerland needs a ~£35 annual motorway vignette, Austria a short-stay one (~€6–11), and Germany's autobahns are free. Ride the scenic back roads and you'll pay almost nothing. See the French toll roads guide below for the lanes, tags and the new barrier-less Flux Libre system.",
                    },
                    {
                        title: "What's not in the number",
                        body: "Insurance and breakdown cover, parking, attractions, the odd coffee and ice cream, and anything you buy along the way — plus the tolls noted above. Add a 10–15% buffer on top of the total and you'll rarely be caught short.",
                    },
                    {
                        title: "£ or €",
                        body: "Toggle the summary between pounds and euros. Conversion is a fixed rate for planning; most on-trip spending is in euros while the crossing and your fuel home are in pounds.",
                    },
                ]}
            />

            <GuideCtaBand
                heading="Got a number? Now build the actual trip."
                body="Turn the estimate into a real plan in Planamoto — day-by-day stops, overnight addresses, and a shared expense tracker that splits fuel, tolls and hotels across the group in every currency as you go."
            />

            <RelatedGuides
                slugs={[
                    "fuel-prices",
                    "french-toll-roads",
                    "packing-europe",
                    "motorbike-tool-kit",
                    "bike-security",
                ]}
            />

            <CtaCard
                title="Plan the trip, split the costs"
                body="Once the budget makes sense, the plan around it — stops, accommodation, and expenses shared across the group in every currency — lives in Planamoto. Browse a curated tour to see how a day looks, or start your own."
                primary={{ label: "Browse tours", to: "/explore" }}
            />
        </GuideArticle>
    );
}

export default EuropeTripCostGuide;
